import type { PartnerClaimStatus } from "../../api/partner_claims";

/**
 * Formatting for the unclaimed worklist. Claim timestamps are epoch **seconds**,
 * the unit the claim table stores; every helper here takes that unit.
 */

const MINUTE = 60;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/**
 * How long a purchase has been stuck, as a coarse duration.
 *
 * Coarse on purpose. The number that matters is "days", because the alarm
 * threshold is 14 of them and the reminder cadence is counted in them — a
 * to-the-minute figure would invite reading precision into a queue that is swept
 * once a day. Under a day still shows hours, since a claim minutes old is
 * usually just the RevenueCat event lagging the redirect and not stuck at all.
 */
export const formatPaidAge = (
  epochSeconds: number | undefined,
  nowSeconds: number = Math.floor(Date.now() / 1000)
): string => {
  if (!epochSeconds) return "—";

  const elapsed = nowSeconds - epochSeconds;
  // A row whose timestamp is ahead of us: clock skew, not a negative age.
  if (elapsed < MINUTE) return "just now";

  const days = Math.floor(elapsed / DAY);
  const hours = Math.floor((elapsed % DAY) / HOUR);

  if (days > 0) return hours > 0 ? `${days}d ${hours}h` : `${days}d`;

  if (hours > 0) return `${hours}h`;
  return `${Math.floor(elapsed / MINUTE)}m`;
};

/**
 * The reminder column: how many sends, and how long ago the last one was.
 *
 * Both halves are load-bearing. Support is meant to read this *before* pressing
 * resend, because several sends with nothing landing means the stored address is
 * wrong and the answer is a support attach, not another email.
 */
export const formatReminderHistory = (
  reminderCount: number,
  reminderSentAt: number[] | undefined,
  nowSeconds: number = Math.floor(Date.now() / 1000)
): string => {
  if (reminderCount === 0) return "none sent";

  const sends = reminderSentAt ?? [];
  const last = sends[sends.length - 1];
  if (!last) {
    // A hand-repaired row can carry a count with no history behind it.
    return `${reminderCount} · times unknown`;
  }

  const age = formatPaidAge(last, nowSeconds);
  // `formatPaidAge` answers with a phrase rather than a duration for a send
  // under a minute old, and "last just now ago" is not English. Reachable right
  // after any send, automated or not.
  const when = age === "just now" ? "just now" : `${age} ago`;

  return `${reminderCount} · last ${when}`;
};

/** Absolute times for the tooltip, so a reader can line sends up against a cadence. */
export const formatSendTimes = (
  reminderSentAt: number[] | undefined
): string => {
  // Tolerates an absent array, not just an empty one: DynamoDB omits an empty
  // list, so a never-reminded or hand-repaired row can arrive without the
  // attribute — and a throw here takes down the whole table, not one cell.
  const sends = reminderSentAt ?? [];
  return sends.length === 0
    ? "No sends recorded"
    : sends.map((at) => new Date(at * 1000).toLocaleString()).join("\n");
};

/** The lifetime cap the backend enforces. Shown so the count reads as "3 of 10". */
export const RESEND_MAX_SENDS = 10;

/**
 * What a claim's status means for someone looking at it here, and what to do.
 *
 * `created` is the one worth spelling out. It means no payment was ever
 * observed, which is either a buyer who abandoned checkout — nothing owed, no
 * action — or a payment Stripe took that never reached us. The second is a
 * stalled pipeline, and attaching would grant a membership against a payment we
 * have no record of while removing the row from the worklist that would have
 * surfaced it. So it is escalated, not attached.
 */
export const statusMeaning = (status: PartnerClaimStatus): string => {
  switch (status) {
    case "created":
      return "No payment recorded. Either checkout was abandoned, or it went through and never reached us — check Stripe, and escalate rather than attaching if it did.";
    case "claimed":
      return "Already claimed by an account.";
    case "attached_by_support":
      return "Already attached to an account by support.";
    default:
      return status;
  }
};

/**
 * Order statuses are worked in, not alphabetical.
 *
 * `paid` first because those are the rows something can be done about here.
 * `created` next: it needs a decision, but the decision is to check Stripe and
 * escalate rather than to act. The resolved states last — they are history, kept
 * visible so a search for a purchase someone already fixed says so rather than
 * finding nothing.
 */
const STATUS_ORDER: Record<string, number> = {
  paid: 0,
  created: 1,
  claimed: 2,
  attached_by_support: 3,
};

export type ClaimSort = "oldest" | "status";

/**
 * Sorts a claim list without mutating it.
 *
 * Age is the tiebreak under either mode, and the whole ordering under the
 * default, because the worklist's premise is that the longest-stuck member
 * matters most. Sorting by status only changes which group they sit in.
 *
 * An unrecognised status sorts last rather than being dropped: a status this
 * build has not heard of is still a row someone needs to see.
 */
export const sortClaims = <
  T extends { status: string; paidAt?: number; createdAt: number },
>(
  claims: T[],
  sort: ClaimSort
): T[] => {
  const age = (claim: T) => claim.paidAt ?? claim.createdAt;
  return [...claims].sort((a, b) => {
    if (sort === "status") {
      const rank =
        (STATUS_ORDER[a.status] ?? 99) - (STATUS_ORDER[b.status] ?? 99);
      if (rank !== 0) return rank;
    }
    return age(a) - age(b);
  });
};

/**
 * How a search hit was found, in words rather than a field name.
 *
 * Shown beside each candidate because it is half the evidence: a hit on an exact
 * checkout session is a certainty, while a hit on four digits is a coincidence
 * until a human agrees with it. Support should be able to tell those apart at a
 * glance.
 */
export const matchedOnLabel = (matchedOn: string): string => {
  switch (matchedOn) {
    case "claimId":
      return "claim id";
    case "checkoutSession":
      return "checkout session";
    case "subscription":
      return "subscription id";
    case "customer":
      return "customer id";
    case "cardLast4":
      return "card last 4";
    case "email":
      return "email";
    case "name":
      return "name on card";
    default:
      return matchedOn;
  }
};
